import { useState, useEffect, useContext } from 'react';
import api from '../services/api';
import { 
  Plus, 
  Search, 
  FileText, 
  CheckCircle2, 
  Clock, 
  AlertCircle, 
  ShoppingBag, 
  X, 
  Save, 
  Trash2, 
  Printer, 
  ChevronLeft,
  ChevronRight,
  CheckSquare,
  Square
} from 'lucide-react';
import { AuthContext } from '../contexts/AuthContext';
import { useSettings } from '../contexts/SettingsContext';

export default function Purchases() {
  const { currencySymbol } = useSettings();
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedIds, setSelectedIds] = useState(new Set());
  
  const { user } = useContext(AuthContext);
  const isAdmin = user?.role === 'admin';

  // New PO State
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [poItems, setPoItems] = useState([]);
  const [productSearch, setProductSearch] = useState('');
  const [listSearch, setListSearch] = useState('');

  const loadData = async () => {
    setLoading(true);
    try {
      const [posRes, suppliersRes, productsRes] = await Promise.all([
        api.get('/purchases'),
        api.get('/suppliers'),
        api.get('/products')
      ]);
      setPos(posRes.data);
      setSuppliers(suppliersRes.data);
      setProducts(productsRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const addItem = (product) => {
    const existing = poItems.find(i => i.productId === product.id);
    if (existing) {
      setPoItems(poItems.map(i => i.productId === product.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setPoItems([...poItems, { productId: product.id, name: product.name, quantity: 1, costPrice: product.cost || 0 }]);
    }
  };

  const updateItem = (productId, field, value) => {
    setPoItems(poItems.map(i => i.productId === productId ? { ...i, [field]: value } : i));
  };

  const removeItem = (productId) => {
    setPoItems(poItems.filter(i => i.productId !== productId));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedSupplier) return alert('Select a vendor');
    if (poItems.length === 0) return alert('Add items to the order');
    try {
      await api.post('/purchases', { supplierId: selectedSupplier, items: poItems });
      setIsModalOpen(false);
      setPoItems([]);
      setSelectedSupplier('');
      loadData();
    } catch (err) {
      alert('Error creating PO');
    }
  };

  const handleReceive = async (id) => {
    if (!window.confirm('Mark this order as received? This will update your inventory stock levels.')) return;
    try {
      await api.post(`/purchases/${id}/receive`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Error receiving PO');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this purchase order?')) return;
    try {
      await api.delete(`/purchases/${id}`);
      loadData();
    } catch (err) {
      alert(err.response?.data?.error || 'Error deleting PO');
    }
  };

  const handleBulkDelete = async () => {
    if (!window.confirm(`Are you sure you want to delete ${selectedIds.size} purchase orders?`)) return;
    try {
      await api.post('/purchases/bulk-delete', { ids: Array.from(selectedIds) });
      setSelectedIds(new Set());
      loadData();
    } catch (err) {
      alert('Error deleting purchase orders');
    }
  };

  const filteredPos = pos.filter(po => 
    po.orderNumber.toLowerCase().includes(listSearch.toLowerCase()) || 
    po.supplier.name.toLowerCase().includes(listSearch.toLowerCase())
  );

  // Pagination Logic
  const totalItems = filteredPos.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentPos = filteredPos.slice(indexOfFirstItem, indexOfLastItem);

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) newSelected.delete(id);
    else newSelected.add(id);
    setSelectedIds(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === currentPos.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(currentPos.map(p => p.id)));
    }
  };

  const poTotal = poItems.reduce((acc, i) => acc + (i.costPrice * i.quantity), 0);

  return (
    <div className="space-y-6 animate-fade-in-up pb-10">
      <div className="flex justify-between items-end mb-8">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight">Stock Replenishment</h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-[10px]">Create purchase orders and manage incoming inventory.</p>
        </div>
        <div className="flex gap-3">
          {isAdmin && selectedIds.size > 0 && (
            <button 
              onClick={handleBulkDelete}
              className="bg-rose-500 hover:bg-rose-600 text-white px-5 py-3 rounded-2xl shadow-lg font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95 animate-bounce-in"
            >
              <Trash2 size={18} /> Delete Selected ({selectedIds.size})
            </button>
          )}
          <button
            onClick={() => setIsModalOpen(true)}
            className="bg-brand hover:bg-brand-light text-white px-6 py-2.5 rounded-xl font-black text-sm transition-all shadow-lg flex items-center gap-2 uppercase tracking-widest"
          >
            <Plus size={18} /> New Purchase Order
          </button>
        </div>
      </div>

      <div className="bg-theme-card rounded-[2rem] shadow-soft border border-theme overflow-hidden">
        <div className="p-4 border-b border-theme bg-theme-main/10 flex justify-between items-center">
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" size={16} />
            <input
              type="text"
              placeholder="Search orders..."
              className="w-full pl-10 pr-4 py-2 bg-theme-main border border-theme rounded-xl text-xs outline-none focus:ring-1 focus:ring-brand text-theme-main font-bold"
              value={listSearch}
              onChange={e => { setListSearch(e.target.value); setCurrentPage(1); }}
            />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-theme-main/20 text-theme-muted text-[10px] uppercase tracking-widest font-black border-b border-theme">
                {isAdmin && (
                  <th className="pl-6 py-4 w-10">
                    <button onClick={toggleSelectAll} className="text-brand">
                      {selectedIds.size === currentPos.length && currentPos.length > 0 ? <CheckSquare size={20} /> : <Square size={20} />}
                    </button>
                  </th>
                )}
                <th className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>Order #</th>
                <th className="px-6 py-4">Vendor</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Total Amount</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-8 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-theme">
              {currentPos.map(po => (
                <tr key={po.id} className={`hover:bg-theme-main/5 transition-colors group ${selectedIds.has(po.id) ? 'bg-brand/5' : ''}`}>
                  {isAdmin && (
                    <td className="pl-6 py-4">
                      <button onClick={() => toggleSelect(po.id)} className={selectedIds.has(po.id) ? 'text-brand' : 'text-theme-muted opacity-30 hover:opacity-100'}>
                        {selectedIds.has(po.id) ? <CheckSquare size={20} /> : <Square size={20} />}
                      </button>
                    </td>
                  )}
                  <td className={isAdmin ? 'px-4 py-4' : 'px-6 py-4'}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-theme-main/10 text-theme-muted flex items-center justify-center border border-theme">
                        <FileText size={18} />
                      </div>
                      <span className="font-black text-theme-main text-sm">{po.orderNumber}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="font-bold text-theme-main">{po.supplier.name}</div>
                    <div className="text-[10px] text-theme-muted uppercase font-black">{po.supplier.contact}</div>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest ${po.status === 'RECEIVED' ? 'bg-emerald-500/10 text-emerald-500' :
                        po.status === 'CANCELLED' ? 'bg-rose-500/10 text-rose-500' :
                          'bg-amber-500/10 text-amber-500 animate-pulse'
                      }`}>
                      {po.status === 'RECEIVED' ? <CheckCircle2 size={12} /> : po.status === 'PENDING' ? <Clock size={12} /> : <AlertCircle size={12} />}
                      {po.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 font-black text-theme-main">{currencySymbol}{po.totalAmount.toFixed(2)}</td>
                  <td className="px-6 py-4 text-xs text-theme-muted font-medium">{new Date(po.createdAt).toLocaleDateString()}</td>
                  <td className="px-8 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      {po.status === 'PENDING' && (
                        <button onClick={() => handleReceive(po.id)} className="px-3 py-1.5 bg-emerald-500 text-white rounded-lg text-[10px] font-black uppercase tracking-widest shadow-md hover:bg-emerald-600 transition-all">Receive</button>
                      )}
                      <button className="p-2 text-theme-muted hover:text-brand hover:bg-brand/10 rounded-lg transition-all"><Printer size={16} /></button>
                      {isAdmin && (
                        <button onClick={() => handleDelete(po.id)} className="p-2 text-theme-muted hover:text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all"><Trash2 size={16} /></button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {currentPos.length === 0 && !loading && (
                <tr>
                  <td colSpan={isAdmin ? "7" : "6"} className="px-6 py-12 text-center text-theme-muted italic">No purchase orders found.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        {totalPages > 1 && (
          <div className="px-8 py-6 bg-theme-main/5 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-xs font-bold text-theme-muted uppercase tracking-widest">
              Showing <span className="text-theme-main">{indexOfFirstItem + 1}</span> to <span className="text-theme-main">{Math.min(indexOfLastItem, totalItems)}</span> of <span className="text-theme-main">{totalItems}</span> orders
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20"><ChevronLeft size={20} /></button>
              {(() => {
                const pages = [];
                let start = Math.max(1, currentPage - 1);
                let end = Math.min(totalPages, start + 2);
                if (end - start < 2) start = Math.max(1, end - 2);
                for (let i = start; i <= end; i++) pages.push(i);
                return pages.map(p => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-10 h-10 rounded-xl font-black text-xs transition-all ${
                      currentPage === p 
                        ? 'bg-brand text-white shadow-lg shadow-brand/30' 
                        : 'bg-theme-card border border-theme text-theme-muted hover:border-brand'
                    }`}
                  >
                    {p}
                  </button>
                ));
              })()}
              <button onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} className="p-2 rounded-xl bg-theme-card border border-theme text-theme-muted disabled:opacity-20"><ChevronRight size={20} /></button>
            </div>
          </div>
        )}
      </div>

      {/* Create PO Modal remains largely the same */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md flex items-start justify-center pt-24 z-50 animate-fade-in p-4">
          <div className="bg-theme-card rounded-3xl shadow-2xl w-full max-w-5xl border border-theme flex flex-col mb-10">
            <div className="px-6 py-4 border-b border-theme bg-theme-main/10 flex justify-between items-center">
              <h3 className="text-xl font-black text-theme-main uppercase tracking-tight flex items-center gap-2"><ShoppingBag className="text-brand" /> Create Purchase Order</h3>
              <button onClick={() => setIsModalOpen(false)} className="text-theme-muted hover:text-theme-main transition-colors"><X size={28} /></button>
            </div>
            <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
              <div className="w-full md:w-80 border-r border-theme bg-theme-main/5 flex flex-col">
                <div className="p-4 border-b border-theme">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-theme-muted" size={16} />
                    <input type="text" placeholder="Search products..." className="w-full pl-10 pr-4 py-2 bg-theme-main border border-theme rounded-xl text-xs outline-none focus:ring-1 focus:ring-brand text-theme-main font-bold" value={productSearch} onChange={e => setProductSearch(e.target.value)} />
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1 custom-scrollbar">
                  {products.filter(p => p.name.toLowerCase().includes(productSearch.toLowerCase())).map(p => (
                    <button key={p.id} onClick={() => addItem(p)} className="w-full text-left p-3 rounded-xl hover:bg-brand/10 group transition-all border border-transparent hover:border-brand/20">
                      <div className="font-bold text-theme-main text-sm group-hover:text-brand">{p.name}</div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[10px] text-theme-muted uppercase font-black">Stock: {p.stock}</span>
                        <span className="text-[10px] text-brand font-black">{currencySymbol}{p.cost?.toFixed(2)}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex-1 flex flex-col p-6 space-y-6 overflow-y-auto custom-scrollbar">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] font-black text-theme-muted uppercase tracking-widest mb-2">Select Vendor</label>
                    <select value={selectedSupplier} onChange={e => setSelectedSupplier(e.target.value)} className="w-full bg-theme-main border border-theme rounded-xl py-2.5 px-4 text-theme-main font-bold focus:ring-2 focus:ring-brand outline-none transition-all">
                      <option value="">Choose a Vendor...</option>
                      {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                </div>
                <div className="flex-1">
                  <h4 className="text-[10px] font-black text-theme-muted uppercase tracking-[0.2em] mb-4">Order Items</h4>
                  <div className="space-y-3">
                    {poItems.map(item => (
                      <div key={item.productId} className="flex items-center gap-4 bg-theme-main/10 p-4 rounded-2xl border border-theme">
                        <div className="flex-1"><div className="text-sm font-bold text-theme-main">{item.name}</div></div>
                        <div className="w-24">
                          <label className="block text-[9px] font-black text-theme-muted uppercase mb-1">Unit Cost</label>
                          <input type="number" step="0.01" value={item.costPrice} onChange={e => updateItem(item.productId, 'costPrice', parseFloat(e.target.value))} className="w-full bg-theme-card border border-theme rounded-lg py-1.5 px-3 text-xs font-black text-brand outline-none" />
                        </div>
                        <div className="w-20">
                          <label className="block text-[9px] font-black text-theme-muted uppercase mb-1">Quantity</label>
                          <input type="number" value={item.quantity} onChange={e => updateItem(item.productId, 'quantity', parseInt(e.target.value))} className="w-full bg-theme-card border border-theme rounded-lg py-1.5 px-3 text-xs font-black text-theme-main outline-none" />
                        </div>
                        <div className="w-24 text-right">
                          <label className="block text-[9px] font-black text-theme-muted uppercase mb-1">Subtotal</label>
                          <div className="text-sm font-black text-theme-main">{currencySymbol}{(item.costPrice * item.quantity).toFixed(2)}</div>
                        </div>
                        <button onClick={() => removeItem(item.productId)} className="p-2 text-rose-500 hover:bg-rose-500/10 rounded-lg transition-all"><Trash2 size={16} /></button>
                      </div>
                    ))}
                    {poItems.length === 0 && (
                      <div className="py-12 border-2 border-dashed border-theme rounded-3xl flex flex-col items-center justify-center text-theme-muted">
                        <ShoppingBag size={48} className="opacity-10 mb-4" /><p className="font-bold uppercase text-[10px] tracking-widest">No items added to order</p>
                      </div>
                    )}
                  </div>
                </div>
                <div className="pt-6 border-t border-theme flex flex-col md:flex-row justify-between items-center gap-6">
                  <div className="text-center md:text-left"><p className="text-[10px] font-black text-theme-muted uppercase tracking-widest mb-1">Total Estimated Cost</p><p className="text-3xl font-black text-brand">{currencySymbol}{poTotal.toFixed(2)}</p></div>
                  <div className="flex gap-3 w-full md:w-auto">
                    <button type="button" onClick={() => setIsModalOpen(false)} className="flex-1 md:flex-none px-8 py-3 bg-theme-card border border-theme text-theme-muted rounded-xl font-black uppercase text-[10px] tracking-widest hover:bg-theme-main transition-colors">Cancel</button>
                    <button onClick={handleSubmit} className="flex-1 md:flex-none px-12 py-3 bg-brand text-white rounded-xl font-black uppercase text-[10px] tracking-widest shadow-xl shadow-brand/20 hover:bg-brand-light transition-all flex items-center justify-center gap-2"><Save size={18} /> Confirm Order</button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
